import React, { Fragment } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Skeleton, Table, Tooltip } from 'antd';
import moment from 'moment';
import './Table.less';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { CampaignItemDto } from '@api/Api';
import { setFavoriteCampaignAction } from '@redux/actions/campaignAction';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { ActionToast } from '@components/Toasts';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import CampaignDropDownMenu from '@components/EntitiesDropdownMenu/CampaignDropDownMenu';
import { AddMediaIcon, FavoriteIcon } from '@icons';

function CampaignsListSkeleton() {
  return (
    <div className="assets_card_container" style={{ height: 56 }}>
      <div className="assets_card_container__preview">
        <Skeleton.Input style={{ width: '100%', height: 56 }} />
      </div>
    </div>
  );
}

function CampaignsTable({
  list,
  countToLoad,
  page,
  countToFetch,
  listLoading
}: {
  list: {
    node: CampaignItemDto;
    cursor: string;
  }[];
  countToLoad: string[];
  page: string;
  countToFetch: number;
  listLoading: boolean;
}) {
  const { tourData } = useOnboardingProcess();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (text: string, { node }: { node: CampaignItemDto }) => {
        const isCampaignDeleted = node.deletedAt;
        const assetsArr = [...(node?.topAssets || [])];
        const assetsCount = node?.totalAssetsCount || 0;
        const imageUrl = assetPreviewThumbnails({
          url: assetsArr?.[0]?.previewUrl || '',
          status: assetsArr?.[0]?.status,
          type: assetsArr?.[0]?.type,
          format: 'img'
        });

        return (
          <div className="campaing_name_container">
            <div>
              {assetsArr.length > 0 ? (
                <div className={classNames('asset', { isCampaignDeleted })}>
                  {assetsCount > 1 && !node.isPrivate && (
                    <div className="hasMore">+{assetsCount - 1}</div>
                  )}
                  {typeof imageUrl === 'string' ? (
                    <img src={imageUrl || ''} alt="" />
                  ) : (
                    imageUrl
                  )}
                </div>
              ) : (
                <div
                  className={classNames('asset_empty', { isCampaignDeleted })}
                >
                  <AddMediaIcon />
                </div>
              )}

              <div className="title">{node.name}</div>
            </div>
            {node && !isCampaignDeleted && (
              <div>
                <Tooltip
                  placement="bottomRight"
                  overlayClassName="toggle_favorite_overlay"
                  title={
                    !node.isFavorite
                      ? 'Add to favorite'
                      : 'Remove from favorite'
                  }
                >
                  <div
                    className="favorite_icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCampaignDeleted) return;
                      if (!node?.isFavorite) {
                        dispatch(
                          setFavoriteCampaignAction({
                            campaignId: node.id,
                            isFavorite: !node.isFavorite
                          })
                        );

                        return;
                      }
                      dispatch(
                        setFavoriteCampaignAction({
                          campaignId: node.id,
                          isFavorite: !node.isFavorite
                        })
                      );
                      toast(
                        <ActionToast
                          title="Project has been removed from favorites"
                          onUndo={undefined}
                          description={undefined}
                          closeToast={undefined}
                        />,
                        {
                          hideProgressBar: true,
                          bodyClassName: 'toast_container--invitation'
                        }
                      );
                    }}
                  >
                    <FavoriteIcon
                      className={classNames(
                        node.isFavorite ? 'favorite' : 'non_favorite'
                      )}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Privacy',
      dataIndex: 'privacy',
      key: 'privacy',
      width: 190,
      render: (text: string, { node }: { node: CampaignItemDto }) => {
        return (
          <div className="campaing_name_container">
            <div className="title">{node.isPrivate ? 'Private' : 'Shared'}</div>
          </div>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 190,
      render: (text: string, { node }: { node: CampaignItemDto }) => {
        return (
          <div className="campaing_name_container">
            <div className="title">
              {moment(node.createdAt).format('DD/MM/YYYY')}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Modified',
      key: 'modified',
      dataIndex: 'modified',
      width: 160,
      render: (text: string, { node }: { node: CampaignItemDto }) => (
        <div className="campaing_name_container">
          <div className="title">{moment(node.lastEventTime).fromNow()}</div>
        </div>
      )
    },
    {
      title: '',
      key: 'action',
      render: (text: string, { node }: { node: CampaignItemDto }) => (
        <CampaignDropDownMenu
          campaign={node}
          overlayClassName="asset_links_overlay"
        />
      )
    }
  ];
  return (
    <div
      className="table_container"
      style={{
        paddingRight: 34,
        paddingLeft: 34,
        marginRight: -34,
        marginLeft: -34
      }}
    >
      {!listLoading && countToLoad.length > 0 ? (
        <Table
          locale={{
            emptyText: countToLoad.map((_, i) => (
              <Fragment key={i}>
                <CampaignsListSkeleton /> <br />
              </Fragment>
            ))
          }}
          onRow={(record) => ({
            className: classNames({
              'tour-route-for-approval-start tour-exit': record?.node.demo,
              'b-outline': tourData?.type === 'route-for-approval'
            }),
            onClick: () => {
              if (!record.node.deletedAt) {
                navigate(`/campaigns/single/${record.node.id}`);
              }
            }
          })}
          rowKey={({ node }: { node: CampaignItemDto }) => node?.id}
          pagination={false}
          columns={columns}
          dataSource={list}
        />
      ) : (
        countToLoad.map((el, idx) => (
          <div key={idx} className="skeleton">
            <Skeleton.Input
              style={{ width: '100%', height: 56, marginBottom: 27 }}
            />{' '}
            <br />{' '}
          </div>
        ))
      )}
      {new Array(countToFetch).fill('').map((el, idx) => (
        <div key={idx} className="skeleton">
          <Skeleton.Input
            style={{ width: '100%', height: 56, marginBottom: 27 }}
          />{' '}
          <br />{' '}
        </div>
      ))}
    </div>
  );
}

export default CampaignsTable;
